import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SiteMapCanvas from '../../components/SiteMapCanvas';
import GatesTable from '../../components/GatesTable';
import GateActionsMenu from '../../components/GateActionsMenu';
import GateConfigMenu from '../../components/GateConfigMenu';
import AssignToGroupDialog from '../../components/AssignToGroupDialog';
import BulkCommandModal from '../../components/BulkCommandModal';
import ShelyUrls from '../../components/ShelyUrls';
import ResidentialSiteAdmin from './ResidentialSiteAdmin';

const LIVE_POLL_INTERVAL_MS = 30000;

function SiteMap() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showGateModal, setShowGateModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGateIds, setSelectedGateIds] = useState([]);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [bulkConfigOpen, setBulkConfigOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkCommandType, setBulkCommandType] = useState(null);
  const liveTimer = useRef(null);

  useEffect(() => {
    axios.get(`/api/admin/sites/${id}`).then(res => setSite(res.data));
  }, [id]);

  const loadSite = () => {
    axios.get(`/api/admin/sites/${id}`).then(res => setSite(res.data));
  };

  // Same live polling pattern as the customer site map. Pause it
  // while the admin is editing the layout — they're moving things on
  // the canvas, a /live response landing in the middle of a drag
  // would be jarring.
  const siteLoaded = site !== null;
  useEffect(() => {
    if (!siteLoaded || editMode) return undefined;
    const poll = () => {
      axios.get(`/api/admin/sites/${id}/live`).then(res => {
        const liveById = new Map(res.data.gates.map(g => [g.id, g]));
        setSite(prev => prev && ({
          ...prev,
          gates: prev.gates.map(g => {
            const live = liveById.get(g.id);
            return live ? { ...g, ...live } : g;
          }),
        }));
      }).catch(() => {});
    };
    liveTimer.current = setInterval(poll, LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(liveTimer.current);
  }, [id, siteLoaded, editMode]);

  const clearSelection = () => {
    setSelectionMode(false);
    setSelectedGateIds([]);
  };

  const toggleSelection = (gateId) => {
    setSelectedGateIds(prev =>
      prev.includes(gateId) ? prev.filter(x => x !== gateId) : [...prev, gateId]
    );
  };

  const hasSelection = selectedGateIds.length > 0 && !editMode;

  const handleLayoutChange = async (layout) => {
    await axios.put(`/api/admin/sites/${id}/layout`, { layout });
    setSite(prev => ({ ...prev, layout }));
  };

  const handleGateMove = async (gateId, x, y) => {
    await axios.put(`/api/admin/gates/${gateId}`, { position_x: x, position_y: y });
    setSite(prev => ({
      ...prev,
      gates: prev.gates.map(g =>
        g.id === gateId ? { ...g, position_x: x, position_y: y } : g
      ),
    }));
  };

  const handleGateResize = async (gateId, scale) => {
    await axios.put(`/api/admin/gates/${gateId}`, { gate_scale: scale });
    setSite(prev => ({
      ...prev,
      gates: prev.gates.map(g =>
        g.id === gateId ? { ...g, gate_scale: scale } : g
      ),
    }));
  };

  const handleGateClick = (gate) => {
    if (editMode) return;
    if (selectionMode) {
      toggleSelection(gate.id);
    } else {
      navigate(`/gates/${gate.id}`);
    }
  };

  const handleGateDelete = async (gate) => {
    if (!window.confirm(`למחוק את השער "${gate.name}"? הפעולה תמחק גם את האירועים וההתראות שלו ולא ניתנת לביטול.`)) return;
    try {
      await axios.delete(`/api/admin/gates/${gate.id}`);
      loadSite();
    } catch (err) {
      alert(err.response?.data?.error || 'מחיקה נכשלה');
    }
  };

  if (!site) return <div className="loading">טוען...</div>;

  // Phase 5.C — residential sites get a flat list view (no map, no
  // groups, no industrial chrome). Everything below this point assumes
  // a business site.
  if (site.type === 'residential') {
    return <ResidentialSiteAdmin site={site} onReload={loadSite} />;
  }

  const totalGates = site.gates.length;
  // not_started gates have no live state yet — they're neither open nor
  // "healthy/closed", so keep them out of every status counter (they
  // still show in the total). See CustomerSiteMap for the rationale.
  const notStartedGates = site.gates.filter(g => g.installation_status === 'not_started').length;
  const openGates = site.gates.filter(g => g.installation_status !== 'not_started' && g.is_open).length;
  const warningGates = site.gates.filter(g => g.cycle_limit && g.cycle_count / g.cycle_limit >= 0.9).length;

  return (
    <>
      <div className="breadcrumb">
        <Link to="/customers">לקוחות</Link>
        <span>/</span>
        <Link to={`/customers/${site.customer_id}`}>לקוח</Link>
        <span>/</span>
        <span>{site.name}</span>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="number">{totalGates}</div>
          <div className="label">שערים</div>
        </div>
        <div className="stat-card green">
          <div className="number">{totalGates - openGates - warningGates - notStartedGates}</div>
          <div className="label">תקינים</div>
        </div>
        <div className="stat-card yellow">
          <div className="number">{warningGates}</div>
          <div className="label">דורשים תשומת לב</div>
        </div>
        <div className="stat-card red">
          <div className="number">{openGates}</div>
          <div className="label">פתוחים</div>
        </div>
      </div>

      <TechnicianAssignmentCard
        site={site}
        onChange={(updated) => setSite(prev => ({
          ...prev,
          assigned_technician_user_id: updated.assigned_technician_user_id,
          assigned_technician: updated.assigned_technician,
        }))}
      />

      {/* Map section */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div className="section-header">
          <h3>מפת האתר</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!editMode && (
              <Link
                to={`/sites/${id}/groups`}
                className="btn btn-sm btn-secondary"
                style={{ textDecoration: 'none' }}
              >
                📁 קבוצות
              </Link>
            )}
            {!editMode && (
              <button
                className={`btn btn-sm ${selectionMode ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => (selectionMode ? clearSelection() : setSelectionMode(true))}
                title="במצב בחירה, לחיצה על שער במפה מסמנת אותו במקום להיכנס לעמוד שלו"
              >
                {selectionMode ? 'בטל בחירה במפה' : 'בחירה במפה'}
              </button>
            )}
            {editMode && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowGateModal(true)}>+ שער חדש</button>
            )}
            <button
              className={`btn btn-sm ${editMode ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setEditMode(!editMode); clearSelection(); }}
            >
              {editMode ? 'סיום עריכה' : 'עריכת מפה'}
            </button>
          </div>
        </div>

        <SiteMapCanvas
          gates={site.gates}
          layout={site.layout}
          editable={editMode}
          onLayoutChange={handleLayoutChange}
          onGateMove={handleGateMove}
          onGateResize={handleGateResize}
          onGateClick={handleGateClick}
          onGateDelete={handleGateDelete}
          selectionMode={!editMode && selectionMode}
          selectedGateIds={editMode ? [] : selectedGateIds}
          showLiveTimer={!editMode}
        />
      </div>

      {/* Gates table — always shown. Checkboxes for bulk-select are
          available only in view mode; edit mode shows the row-level
          delete button instead. */}
      <GatesTable
        gates={site.gates}
        selectedIds={editMode ? [] : selectedGateIds}
        onToggleSelect={editMode ? null : toggleSelection}
        onRowClick={editMode ? null : (g) => navigate(`/gates/${g.id}`)}
        extraHeaderCells={editMode ? <th style={{ width: '1%' }}>פעולות</th> : null}
        renderExtraCells={editMode ? (g) => (
          <td onClick={e => e.stopPropagation()}>
            <button className="btn btn-danger btn-sm" onClick={() => handleGateDelete(g)}>מחק</button>
          </td>
        ) : null}
      />

      {hasSelection && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #e5e7eb',
          padding: '0.85rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.85rem',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.04)',
          zIndex: 30,
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
            {selectedGateIds.length} נבחרו
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => setActionsMenuOpen(true)}
            style={{
              padding: '0.45rem 0.9rem',
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              borderRadius: '6px',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            פעולות
          </button>
          <button
            onClick={clearSelection}
            style={{
              padding: '0.45rem 0.9rem',
              border: 'none',
              background: 'transparent',
              color: '#6b7280',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            ביטול
          </button>
        </div>
      )}

      {actionsMenuOpen && (
        <GateActionsMenu
          gateIds={selectedGateIds}
          onClose={() => setActionsMenuOpen(false)}
          onPickSettings={() => {
            setActionsMenuOpen(false);
            setBulkConfigOpen(true);
          }}
          onPickAssignGroup={() => {
            setActionsMenuOpen(false);
            setBulkAssignOpen(true);
          }}
          onPickOpen={() => {
            setActionsMenuOpen(false);
            setBulkCommandType('open');
          }}
          onPickClose={() => {
            setActionsMenuOpen(false);
            setBulkCommandType('close');
          }}
        />
      )}

      {bulkCommandType && (
        <BulkCommandModal
          gateIds={selectedGateIds}
          gates={site.gates}
          commandType={bulkCommandType}
          apiBase="admin"
          onClose={() => setBulkCommandType(null)}
          onCompleted={() => {
            // Refresh live state so row badges reflect the new commands.
            axios.get(`/api/admin/sites/${id}/live`).then(res => {
              const liveById = new Map(res.data.gates.map(g => [g.id, g]));
              setSite(prev => prev && ({
                ...prev,
                gates: prev.gates.map(g => {
                  const live = liveById.get(g.id);
                  return live ? { ...g, ...live } : g;
                }),
              }));
            }).catch(() => {});
          }}
        />
      )}

      {bulkAssignOpen && (
        <AssignToGroupDialog
          siteId={id}
          gateIds={selectedGateIds}
          apiBase="admin"
          onClose={() => setBulkAssignOpen(false)}
          onApplied={() => {
            setBulkAssignOpen(false);
            loadSite();
            clearSelection();
          }}
        />
      )}

      {bulkConfigOpen && (
        <GateConfigMenu
          gateIds={selectedGateIds}
          initialSettings={null}
          apiBase="admin"
          onClose={() => setBulkConfigOpen(false)}
          onApplied={() => {
            setBulkConfigOpen(false);
            loadSite();
            clearSelection();
          }}
        />
      )}

      {showGateModal && (
        <NewGateModal
          siteId={id}
          onClose={() => setShowGateModal(false)}
          onCreated={() => { setShowGateModal(false); loadSite(); }}
        />
      )}
    </>
  );
}

function NewGateModal({ siteId, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', product_id: '', model: '', sheli_id: '', cycle_limit: ''
  });
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get('/api/admin/products').then(res => setProducts(res.data));
  }, []);

  const handlePickProduct = (productId) => {
    const product = products.find(p => String(p.id) === String(productId));
    setForm(prev => ({
      ...prev,
      product_id: productId,
      model: product ? (product.model_number || product.name) : prev.model,
      cycle_limit: product && product.default_cycle_limit
        ? String(product.default_cycle_limit)
        : prev.cycle_limit,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post(`/api/admin/sites/${siteId}/gates`, {
      ...form,
      product_id: form.product_id || null,
      cycle_limit: form.cycle_limit ? parseInt(form.cycle_limit) : null,
    });
    onCreated();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>שער חדש</h2>
        <div className="form-group">
          <label>שם השער</label>
          <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>מוצר מהקטלוג</label>
          <select value={form.product_id} onChange={e => handlePickProduct(e.target.value)}>
            <option value="">-- בחר מהקטלוג (אופציונלי) --</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.model_number ? ` (${p.model_number})` : ''}{p.category_name ? ` · ${p.category_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>דגם (טקסט חופשי)</label>
          <input value={form.model} onChange={e => setForm({...form, model: e.target.value})} />
        </div>
        <div className="form-group">
          <label>מזהה רכיב</label>
          <input value={form.sheli_id} onChange={e => setForm({...form, sheli_id: e.target.value})} placeholder="SHELI-XXXXXX" />
          <ShelyUrls sheliId={form.sheli_id.trim()} compact hint={false} />
        </div>
        <div className="form-group">
          <label>יעד סייקלים לטיפול</label>
          <input type="number" value={form.cycle_limit} onChange={e => setForm({...form, cycle_limit: e.target.value})} placeholder="10000" />
        </div>
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary">צור שער</button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

function TechnicianAssignmentCard({ site, onChange }) {
  const [technicians, setTechnicians] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/admin/technicians')
      .then(res => setTechnicians(res.data))
      .catch(() => setTechnicians([]));
  }, []);

  const currentId = site.assigned_technician_user_id;

  const handleChange = async (e) => {
    const value = e.target.value;
    const techId = value === '' ? null : Number(value);
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(`/api/admin/sites/${site.id}/technician`, {
        technician_user_id: techId,
      });
      onChange(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בעדכון');
    } finally {
      setSaving(false);
    }
  };

  const currentTechName = site.assigned_technician?.name;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: '0.85rem 1rem',
      marginBottom: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.85rem',
      flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.2rem' }}>
          טכנאי משויך
        </div>
        <div style={{ fontSize: '0.95rem', fontWeight: 500 }}>
          {currentTechName || <span style={{ color: '#9ca3af' }}>לא משויך</span>}
        </div>
        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.2rem' }}>
          הטכנאי המשויך רואה את האתר באפליקציית הטכנאי שלו ומבצע התקנות.
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
        <select
          value={currentId == null ? '' : String(currentId)}
          onChange={handleChange}
          disabled={saving || technicians === null}
          style={{
            padding: '0.5rem 0.7rem',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            fontSize: '0.9rem',
            fontFamily: 'Heebo, sans-serif',
            background: '#fff',
            minWidth: 220,
          }}
        >
          <option value="">ללא</option>
          {(technicians || []).map(t => (
            <option key={t.id} value={t.id}>
              {t.name}{t.email ? ` · ${t.email}` : ''}
            </option>
          ))}
        </select>
        {error && (
          <span style={{ color: '#b91c1c', fontSize: '0.78rem' }}>{error}</span>
        )}
      </div>
    </div>
  );
}


export default SiteMap;
